import React, { useState, useMemo } from "react";
import { useGamePlayTests, useGame } from "@seabrookstudios/pitch2table";
import { s2UserId } from "@seabrookstudios/types";
import { FullScreenDialog } from "../core/FullScreenDialog";
import { SmartTextField } from "../core/SmartTextField";
import { UserName } from "../users/UserName";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import Person from "@mui/icons-material/Person";
import SmartToy from "@mui/icons-material/SmartToy";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import AccountCircle from "@mui/icons-material/AccountCircle";
import Check from "@mui/icons-material/Check";

/**
 * Custom hook to extract previous players from play test data
 * @param {import("@seabrookstudios/types").GameId} gameId
 * @returns {string[]} Array of previous player names
 */
const usePreviousPlayers = (gameId) => {
  const { data: playTests = [] } = useGamePlayTests(gameId);

  return useMemo(() => {
    const playerNames = new Set();

    playTests.forEach((playTest) => {
      if (playTest.outcomes) {
        playTest.outcomes.forEach((outcome) => {
          // Exclude AI, anonymous, and user/designer players
          if (!outcome.ai && !outcome.anonymous && !outcome.user && outcome.name) {
            playerNames.add(outcome.name.trim());
          }
        });
      }
    });

    return Array.from(playerNames).sort();
  }, [playTests]);
};

/**
 * @param {object} props
 * @param {string} props.freeTextName
 * @param {(text: string) => void} props.setFreeTextName
 * @param {() => void} props.onSelect
 */
const FreeTextPlayerOption = ({ freeTextName, setFreeTextName, onSelect }) => {
  return (
    <ListItem>
      <ListItemIcon>
        <Person />
      </ListItemIcon>
      <ListItemText style={{ paddingRight: 16 }}>
        <SmartTextField label="Player Name" initialValue={freeTextName} onChange={setFreeTextName} />
      </ListItemText>
      <ListItemSecondaryAction>
        <IconButton onClick={onSelect} disabled={!freeTextName.trim()}>
          <Check />
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").GameDesigners[]} props.designers
 * @param {string|undefined} props.selectedDesignerId
 * @param {(designerId: string) => void} props.onDesignerSelect
 */
const DesignerPlayerOption = ({ designers, selectedDesignerId, onDesignerSelect }) => {
  if (designers.length === 0) {
    return null;
  }

  return designers.map((designer) => (
    <ListItemButton
      key={designer.designerId}
      onClick={() => onDesignerSelect(designer.designerId)}
      selected={selectedDesignerId === designer.designerId}
    >
      <ListItemIcon>
        <AccountCircle />
      </ListItemIcon>
      <ListItemText>
        <UserName userId={designer.designerId} />
      </ListItemText>
    </ListItemButton>
  ));
};

/**
 * @param {object} props
 * @param {React.ReactElement} props.icon
 * @param {string} props.label
 * @param {() => void} props.onClick
 */
const SimplePlayerOption = ({ icon, label, onClick }) => {
  return (
    <ListItemButton onClick={onClick}>
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText>{label}</ListItemText>
    </ListItemButton>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestPlayer} props.player
 * @param {function} props.onPlayerUpdate
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestPlayer[]} [props.allPlayers] - All players to determine counts
 * @param {import("react").ReactNode} props.children - The trigger element
 */
export const PlayerSelectionModal = ({ gameId, player, onPlayerUpdate, allPlayers = [], children }) => {
  const { data: game } = useGame(gameId);
  const allPreviousPlayers = usePreviousPlayers(gameId);
  const [freeTextName, setFreeTextName] = useState(
    !player.ai && !player.anonymous && !player.user ? player.name || "" : ""
  );
  const [selectedDesignerId, setSelectedDesignerId] = useState(player.user ? player.userId?.toString() : "");
  const [forceClose, setForceClose] = useState(false);

  const handleUpdateAndClose = (playerId, updates) => {
    onPlayerUpdate(playerId, updates);
  };

  const usedPlayers = allPlayers.map((p) => p.name);
  const previousPlayers = allPreviousPlayers.filter((p) => !usedPlayers.includes(p));

  const designers = game?.designers || [];

  const handleFreeTextSelect = () => {
    handleUpdateAndClose(player.id, {
      name: freeTextName,
      ai: false,
      anonymous: false,
      user: false,
      userId: undefined,
    });
    setForceClose(true);
  };

  const handleDesignerSelect = (designerId) => {
    setSelectedDesignerId(designerId);
    handleUpdateAndClose(player.id, {
      name: `Designer ${designerId}`,
      ai: false,
      anonymous: false,
      user: true,
      userId: s2UserId(designerId),
    });
    setForceClose(true);
  };

  const handleAnonymousSelect = () => {
    const anonymousCount = allPlayers.filter((p) => p.anonymous && p.id !== player.id).length + 1;
    handleUpdateAndClose(player.id, {
      name: `Anonymous ${anonymousCount}`,
      ai: false,
      anonymous: true,
      user: false,
      userId: undefined,
    });
    setForceClose(true);
  };

  const handleAISelect = () => {
    const aiCount = allPlayers.filter((p) => p.ai && p.id !== player.id).length + 1;
    handleUpdateAndClose(player.id, {
      name: `AI ${aiCount}`,
      ai: true,
      anonymous: false,
      user: false,
      userId: undefined,
    });
    setForceClose(true);
  };

  const handlePreviousPlayerSelect = (playerName) => {
    handleUpdateAndClose(player.id, {
      name: playerName,
      ai: false,
      anonymous: false,
      user: false,
      userId: undefined,
    });
    setForceClose(true);
  };

  return (
    <FullScreenDialog
      title="Select Player Type"
      CustomControl={<Box>{children}</Box>}
      forceClose={forceClose}
      onOpen={() => setForceClose(false)}
    >
      <List dense>
        <FreeTextPlayerOption
          freeTextName={freeTextName}
          setFreeTextName={setFreeTextName}
          onSelect={handleFreeTextSelect}
        />

        <Divider />

        <DesignerPlayerOption
          designers={designers}
          selectedDesignerId={selectedDesignerId}
          onDesignerSelect={handleDesignerSelect}
        />

        {designers.length > 0 && <Divider />}

        <SimplePlayerOption icon={<VisibilityOff />} label="Anonymous" onClick={handleAnonymousSelect} />
        <SimplePlayerOption icon={<SmartToy />} label="AI" onClick={handleAISelect} />

        {previousPlayers.length > 0 && (
          <>
            <Divider />
            {previousPlayers.map((playerName) => (
              <ListItemButton key={playerName} onClick={() => handlePreviousPlayerSelect(playerName)}>
                <ListItemIcon>
                  <Person />
                </ListItemIcon>
                <ListItemText primary={playerName} />
              </ListItemButton>
            ))}
          </>
        )}
      </List>
    </FullScreenDialog>
  );
};
